/**
 * Reviews Widget
 * Displays judge.me product reviews with custom card design
 *
 * Features:
 * - Uses Shopify metafields for stats (reliable, no API calls)
 * - Embeds judge.me widget for full reviews (handles pagination, voting, etc.)
 * - Custom card design matching product widget system
 * - BOLD, conversion-focused design
 * - Full-width on desktop, responsive on mobile
 * - British English throughout
 *
 * @version 3.0.0
 */

(function(window) {
  'use strict';

  class ReviewsWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      super(manager, container, config);

      // Stats from Shopify metafields (reliable!)
      this.averageRating = parseFloat(config.averageRating) || 0;
      this.totalReviews = parseInt(config.totalReviews) || 0;
      this.ratingScale = {
        min: parseFloat(config.ratingScaleMin) || 1,
        max: parseFloat(config.ratingScaleMax) || 5
      };

      this.modalId = `reviews-modal-${config.id}`;
      this.productId = config.productId || '';

      // Content
      this.content = {
        cardHeading: config.cardHeading || 'Customer Reviews',
        cardSubheading: config.cardSubheading || 'Real feedback from real customers',
        viewButton: config.viewButton || 'View All Reviews',
        modalHeading: config.modalHeading || 'Customer Reviews',
        noReviewsMessage: config.noReviewsMessage || 'No reviews yet. Be the first!'
      };
    }

    /**
     * Initialize widget
     */
    async init() {
      // Hide if no reviews
      if (!this.totalReviews || this.totalReviews === 0) {
        this.hide();
        return;
      }
    }

    /**
     * Render widget
     */
    render() {
      if (!this.totalReviews || this.totalReviews === 0) {
        this.hide();
        return;
      }

      const html = `
        ${this.buildCardHTML()}
        ${this.buildModalHTML()}
      `;

      this.setContent(html);
      this.attachEventListeners();
      this.initializeJudgeMeWidget();
      this.emit('reviews:rendered');
    }

    /**
     * Build card HTML - HIGH IMPACT DESIGN
     */
    buildCardHTML() {
      const fiveStarPercentage = Math.round((this.averageRating / this.ratingScale.max) * 100);

      return `
        <div class="pw-widget pw-reviews-widget" style="width: 100%;">
          <div class="pw-widget--card" style="padding: var(--sp-5);">

            <!-- BOLD Header -->
            <div style="display: flex; align-items: center; gap: var(--sp-3); margin-bottom: var(--sp-5);">
              <div style="width: 56px; height: 56px; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-base-button)); border-radius: var(--rounded-card); flex-shrink: 0;">
                ${this.getStarIconSVG('var(--color-base-button-text)')}
              </div>
              <div style="flex: 1;">
                <h3 class="pw-font-bold" style="margin: 0 0 var(--sp-1) 0; font-size: var(--text-xl); line-height: 1.2; color: rgb(var(--color-drawer-text));">
                  ${this.content.cardHeading}
                </h3>
                <div style="display: flex; align-items: center; gap: var(--sp-2); flex-wrap: wrap;">
                  ${this.buildStarRating(this.averageRating, 'medium')}
                  <span class="pw-font-bold" style="font-size: var(--text-lg); color: rgb(var(--color-drawer-text));">
                    ${this.averageRating.toFixed(1)}
                  </span>
                  <span class="pw-font-semibold" style="font-size: var(--text-base); color: rgb(var(--color-drawer-text) / 0.6);">
                    (${this.totalReviews} ${this.totalReviews === 1 ? 'review' : 'reviews'})
                  </span>
                </div>
              </div>
            </div>

            <!-- Social Proof Summary -->
            <div style="display: flex; align-items: center; justify-content: space-between; gap: var(--sp-4); padding: var(--sp-4); background: rgb(var(--color-base-button) / 0.05); border: 2px solid rgb(var(--color-base-button) / 0.15); border-radius: var(--rounded-card); margin-bottom: var(--sp-4);">
              <div>
                <div class="pw-font-bold" style="font-size: var(--text-2xl); color: rgb(var(--color-drawer-text)); line-height: 1; margin-bottom: var(--sp-1d5);">
                  ${this.totalReviews}
                </div>
                <div class="pw-font-semibold" style="font-size: var(--text-sm); color: rgb(var(--color-drawer-text) / 0.7);">
                  Customer reviews
                </div>
              </div>
              <div style="text-align: right;">
                <div class="pw-font-bold" style="font-size: var(--text-2xl); color: rgb(var(--color-base-button)); line-height: 1; margin-bottom: var(--sp-1d5);">
                  ${fiveStarPercentage}%
                </div>
                <div class="pw-font-semibold" style="font-size: var(--text-sm); color: rgb(var(--color-drawer-text) / 0.7);">
                  Recommend this
                </div>
              </div>
            </div>

            <!-- View All Reviews Button - BOLD CTA -->
            <button
              type="button"
              class="pw-btn pw-btn--primary"
              style="width: 100%; padding: var(--sp-4) var(--sp-5); font-size: var(--text-base); font-weight: 700; gap: var(--sp-2);"
              aria-controls="${this.modalId}"
              aria-expanded="false"
            >
              <span>${this.content.viewButton}</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

          </div>
        </div>
      `;
    }

    /**
     * Build modal HTML - Embeds Judge.me widget
     */
    buildModalHTML() {
      return `
        <x-modal id="${this.modalId}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="Customer Reviews" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${this.modalId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">

            <!-- Header -->
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">${this.content.cardHeading}</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${this.modalId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 14" fill="none">
                    <path d="M15 1.00012L1 13.0001M1 1L15 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>

            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">

                <!-- Hero Section - BOLD STATS -->
                <div class="text-center" style="padding: var(--sp-4) var(--sp-3);">
                  <div style="display: inline-flex; align-items: center; justify-content: center; width: 5rem; height: 5rem; background: rgb(var(--color-base-button)); border-radius: var(--rounded-card); margin-bottom: var(--sp-4); box-shadow: 0 var(--sp-2) var(--sp-5) rgb(var(--color-base-button) / 0.3);">
                    ${this.getStarIconSVG('var(--color-base-button-text)')}
                  </div>
                  <h2 class="heading font-bold" style="font-size: var(--text-3xl); margin-bottom: var(--sp-3); color: rgb(var(--color-drawer-text));">
                    ${this.content.modalHeading}
                  </h2>
                  <div style="display: flex; align-items: center; justify-content: center; gap: var(--sp-3); margin-bottom: var(--sp-2);">
                    ${this.buildStarRating(this.averageRating, 'large')}
                    <span class="pw-font-bold" style="font-size: var(--text-4xl); color: rgb(var(--color-drawer-text));">
                      ${this.averageRating.toFixed(1)}
                    </span>
                  </div>
                  <p class="pw-font-semibold" style="font-size: var(--text-lg); color: rgb(var(--color-drawer-text) / 0.7); margin-bottom: var(--sp-6);">
                    Based on ${this.totalReviews} verified customer ${this.totalReviews === 1 ? 'review' : 'reviews'}
                  </p>
                </div>

                <!-- Judge.me Widget Container -->
                <div style="padding: 0 var(--sp-4);">
                  <div
                    class="jdgm-widget jdgm-review-widget"
                    data-id="${this.productId}"
                    data-auto-install="false"
                  >
                    <!-- Judge.me will populate this -->
                  </div>
                </div>

              </div>
            </div>
          </div>
        </x-modal>
      `;
    }

    /**
     * Build star rating
     */
    buildStarRating(rating, size = 'medium') {
      const numRating = parseFloat(rating) || 0;
      const fullStars = Math.floor(numRating);
      const hasHalfStar = numRating % 1 >= 0.5;
      const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

      const sizeMap = { small: 16, medium: 20, large: 28 };
      const starSize = sizeMap[size] || sizeMap.medium;
      const starColor = 'rgb(var(--color-base-button))';

      return `
        <div style="display: flex; gap: 2px;" aria-label="${numRating} out of 5 stars">
          ${[...Array(fullStars)].map(() =>
            `<span style="color: ${starColor}; font-size: ${starSize}px; line-height: 1;">★</span>`
          ).join('')}
          ${hasHalfStar ? `<span style="color: ${starColor}; font-size: ${starSize}px; line-height: 1;">⯨</span>` : ''}
          ${[...Array(emptyStars)].map(() =>
            `<span style="color: rgb(var(--color-drawer-text) / 0.25); font-size: ${starSize}px; line-height: 1;">☆</span>`
          ).join('')}
        </div>
      `;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
      // Open modal button
      const openBtn = this.container.querySelector('.pw-btn--primary');
      if (openBtn) {
        openBtn.addEventListener('click', () => this.openModal());
      }
    }

    /**
     * Initialize judge.me widget in modal
     */
    initializeJudgeMeWidget() {
      // Wait for modal to be visible before initializing
      const modal = document.getElementById(this.modalId);
      if (!modal) return;

      // Listen for modal open event
      const initWidget = () => {
        if (window.jdgm) {
          setTimeout(() => {
            if (window.jdgm.customizeBadges) {
              window.jdgm.customizeBadges();
            }
            if (window.jdgm.initWidgets) {
              window.jdgm.initWidgets();
            }
          }, 100);
        }
      };

      // Initialize when modal opens
      modal.addEventListener('show', initWidget, { once: false });

      // Also try initializing immediately
      initWidget();
    }

    /**
     * Open modal
     */
    openModal() {
      const modal = document.getElementById(this.modalId);
      if (modal && modal.show) {
        modal.show();
      }
    }

    /**
     * SVG Icons
     */
    getStarIconSVG(colourRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: 28px; height: 28px; color: rgb(${colourRGB});" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
        </svg>
      `;
    }
  }

  if (window.ProductWidgets && window.ProductWidgets.widgets) {
    window.ProductWidgets.widgets.ReviewsWidget = ReviewsWidget;
  }

})(window);
